import {
  DeviceMdmActionsResponse as DeviceMdmActionsDto,
  Employee as EmployeeDto,
  Group as GroupDto,
  GroupMembershipTypeEnum,
  IntakeForm as IntakeFormDto,
  MicrosoftTenantResponse as MicrosoftTenantDto,
  OrganizationResellerResponse as OrganizationResellerDto,
  WorkflowStatus as WorkflowStatusDto,
  WorkflowStatusEnum,
} from '@electricjs/core_entity-client';
import { http, HttpResponse, delay } from 'msw';

import { simpleEmployee } from '@/mocks/data/employeeData';
import {
  buildMockMicrosoftTenants,
  buildMockOrganizationResellers,
  simpleOrganizationGroups,
  simpleOrganization,
} from '@/mocks/data/organizationData';
import { mockOrganizationEmployees } from './employeeHandlers';

interface AddEmployeesResponse {
  description: string;
  added_employees: EmployeeDto[];
  failed_employee_emails: string[];
}

// Group transfer settings persist across PUT → GET so toggling them on the
// Employee updates page is reflected in places that read them (e.g. the group
// reparent confirmation modal).
const groupTransferSettings = {
  auto_grant_access_on_group_join: false,
  auto_revoke_access_on_group_leave: false,
};

// In-memory stores so create/archive survive refetches until page reload.
let microsoftTenants = buildMockMicrosoftTenants();
let organizationResellers = buildMockOrganizationResellers();
// Seeded as already completed so the GET returns 200 instead of a 404, which would look like an unmocked endpoint.
// (The app reads a 404 as "intake form not filled yet" and shows the form.)
let intakeForm: IntakeFormDto | undefined = {
  id: 'intake-form-mock',
  organization_id: simpleOrganization.id,
  employee_id: simpleEmployee.id,
  answers: {},
  created_at: new Date().toISOString(),
};
const lookedUpResellers = new Map<string, OrganizationResellerDto>();

export const organizationHandlers = [
  http.get('*/v1/organizations', async () => {
    await delay();
    return HttpResponse.json(
      { results: [simpleOrganization], total: 1 },
      { status: 200 }
    );
  }),

  http.get('*/v1/organizations/:id', async () => {
    // As example, returning always the same organization for any given id:
    await delay();
    return HttpResponse.json(simpleOrganization, { status: 200 });

    //If needed (in future cases), we can be more realistic:
    // if (params.id === simpleOrganization.id) {
    //   await delay();
    //   return HttpResponse.json(simpleOrganization, { status: 200 });
    // } else {
    //   return new HttpResponse(null, { status: 404 }); // returning "not found" for other given ids
    // }
  }),

  // Note: GET /v1/organizations/:id/employees is handled by employeeHandlers.ts
  // to avoid duplicate handler conflicts

  http.post('*/v1/organizations/:id/employees', async ({ request }) => {
    const body = (await request.json()) as { employees: EmployeeDto[] };

    // Add employees to shared mock data (mutate the data)
    // This ensures GET requests reflect POST mutations
    mockOrganizationEmployees.push(...body.employees);

    const addEmployeesResponse: AddEmployeesResponse = {
      description: 'Employees added successfully',
      added_employees: body.employees,
      failed_employee_emails: [],
    };

    await delay();
    return HttpResponse.json(addEmployeesResponse, { status: 201 });
  }),

  http.get('*/v1/organizations/:id/groups', async () => {
    await delay();
    return HttpResponse.json(
      {
        results: simpleOrganizationGroups,
        total: simpleOrganizationGroups.length,
      },
      { status: 200 }
    );
  }),

  http.get('*/v1/organizations/:orgId/groups/:groupId', async ({ params }) => {
    const group = simpleOrganizationGroups.find(g => g.id === params.groupId);

    await delay();
    if (!group) {
      return HttpResponse.json(null, { status: 404 });
    }
    return HttpResponse.json(group, { status: 200 });
  }),

  // Note: GET /v1/organizations/:id/request_type_categories is handled by
  // requestsHandlers.ts to avoid duplicate handler conflicts.
  // Note: GET /v1/organizations/:id/employees/directory is handled by
  // employeeHandlers.ts (registered earlier) which honors the `status` filter.

  http.get('*/v1/organizations/:id/additional_info', async ({ params }) => {
    await delay();
    return HttpResponse.json(
      {
        organization_id: params.id,
        abm_enrollment_status: 'unknown',
        send_automatic_welcome_emails: false,
        warranty_upgrade_preference: true,
        terms_required: false,
        terms_accepted: false,
        scheduled_requests_time_in_advance: null,
        ...groupTransferSettings,
        applications_automation_preference: 'manual_review',
        hardware_provisioning_preference: 'let_me_decide',
        offboarding_can_be_immediate: false,
        offboarding_relies_on_employee_time_zone: false,
      },
      { status: 200 }
    );
  }),

  http.put(
    '*/v1/organizations/:id/additional_info',
    async ({ params, request }) => {
      await delay();
      const body = (await request.json()) as Record<string, unknown>;
      if (typeof body.auto_grant_access_on_group_join === 'boolean') {
        groupTransferSettings.auto_grant_access_on_group_join =
          body.auto_grant_access_on_group_join;
      }
      if (typeof body.auto_revoke_access_on_group_leave === 'boolean') {
        groupTransferSettings.auto_revoke_access_on_group_leave =
          body.auto_revoke_access_on_group_leave;
      }
      return HttpResponse.json(
        {
          organization_id: params.id,
          abm_enrollment_status: body.abm_enrollment_status ?? 'unknown',
          send_automatic_welcome_emails:
            body.send_automatic_welcome_emails ?? false,
          warranty_upgrade_preference: body.warranty_upgrade_preference ?? true,
          terms_required: body.terms_required ?? false,
          terms_accepted: body.terms_accepted ?? false,
          scheduled_requests_time_in_advance:
            body.scheduled_requests_time_in_advance ?? null,
          ...groupTransferSettings,
          applications_automation_preference:
            body.applications_automation_preference ?? 'manual_review',
          hardware_provisioning_preference:
            body.hardware_provisioning_preference ?? 'let_me_decide',
          offboarding_can_be_immediate:
            body.offboarding_can_be_immediate ?? false,
          offboarding_relies_on_employee_time_zone:
            body.offboarding_relies_on_employee_time_zone ?? false,
        },
        { status: 200 }
      );
    }
  ),

  http.post('*/v1/organizations/:id/groups', async ({ request }) => {
    const body = (await request.json()) as {
      name: string;
      employee_ids?: string[];
      parent_group_id?: string;
      membership_type?: GroupMembershipTypeEnum;
    };
    const now = new Date().toISOString();
    const group: GroupDto = {
      id: `group-${crypto.randomUUID()}`,
      name: body.name,
      created_at: now,
      updated_at: now,
      parent_group_id: body.parent_group_id,
      import_enabled: false,
      employees_count: body.employee_ids?.length ?? 0,
      applications_count: 0,
      membership_type: body.membership_type ?? GroupMembershipTypeEnum.Static,
    };
    simpleOrganizationGroups.push(group);

    await delay();
    return HttpResponse.json({ ...group, employees: [] }, { status: 201 });
  }),

  http.delete(
    '*/v1/organizations/:orgId/groups/:groupId',
    async ({ params }) => {
      const index = simpleOrganizationGroups.findIndex(
        g => g.id === params.groupId
      );

      await delay();
      if (index === -1) {
        return new HttpResponse(null, { status: 404 });
      }
      simpleOrganizationGroups.splice(index, 1);
      return new HttpResponse(null, { status: 204 });
    }
  ),

  // Mock data doesn't model which groups grant which app, so every group is
  // returned for any application.
  http.get(
    '*/v1/organizations/:orgId/organization_applications/:applicationKey/groups',
    async () => {
      await delay();
      return HttpResponse.json(simpleOrganizationGroups, { status: 200 });
    }
  ),

  http.post(
    '*/v1/organizations/:id/packages/send_change_interest_email',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),

  http.patch('*/v1/organizations/:id/users/:userId/role', async () => {
    await delay();
    return new HttpResponse(null, { status: 204 });
  }),

  http.put('*/v1/organizations/:id/applications/:applicationId', async () => {
    await delay();
    return new HttpResponse(null, { status: 204 });
  }),

  http.get(
    '*/v1/organizations/:id/applications/:applicationKey/devices/:deviceId/actions',
    async () => {
      const actions: DeviceMdmActionsDto = { can_lock: true, can_wipe: true };
      await delay();
      return HttpResponse.json(actions, { status: 200 });
    }
  ),

  http.get('*/v1/organizations/:id/intake_form', async () => {
    await delay();
    return intakeForm
      ? HttpResponse.json(intakeForm, { status: 200 })
      : new HttpResponse(null, { status: 404 });
  }),

  http.post(
    '*/v1/organizations/:id/intake_form',
    async ({ params, request }) => {
      const body = (await request.json()) as { answers: object };
      intakeForm = {
        id: `intake-form-${crypto.randomUUID()}`,
        organization_id: String(params.id),
        employee_id: simpleEmployee.id,
        answers: body.answers,
        created_at: new Date().toISOString(),
      };

      await delay();
      return HttpResponse.json(intakeForm, { status: 201 });
    }
  ),

  http.get(
    '*/v2/organizations/:id/workflows/:workflowId/status',
    async ({ params }) => {
      const status: WorkflowStatusDto = {
        workflow_id: String(params.workflowId),
        status: WorkflowStatusEnum.Completed,
      };
      await delay();
      return HttpResponse.json(status, { status: 200 });
    }
  ),

  http.get('*/v1/organizations/:id/microsoft_tenants', async ({ request }) => {
    const url = new URL(request.url);
    const limit = Number(url.searchParams.get('limit') ?? 5);
    const offset = Number(url.searchParams.get('offset') ?? 0);

    await delay();
    return HttpResponse.json(
      {
        results: microsoftTenants.slice(offset, offset + limit),
        total: microsoftTenants.length,
      },
      { status: 200 }
    );
  }),

  http.post(
    '*/v1/organizations/:id/microsoft_tenants',
    async ({ params, request }) => {
      const body = (await request.json()) as {
        name: string;
        ms_tenant_id: string;
        domain: string;
      };
      const now = new Date().toISOString();
      const tenant: MicrosoftTenantDto = {
        id: `microsoft-tenant-${crypto.randomUUID()}`,
        organization_id: String(params.id),
        name: body.name,
        ms_tenant_id: body.ms_tenant_id,
        domain: body.domain,
        created_at: now,
        updated_at: now,
      };
      microsoftTenants = [...microsoftTenants, tenant];

      await delay();
      return HttpResponse.json(tenant, { status: 201 });
    }
  ),

  http.post(
    '*/v1/organizations/:id/microsoft_tenants/:tenantId/archive',
    async ({ params }) => {
      const exists = microsoftTenants.some(t => t.id === params.tenantId);
      microsoftTenants = microsoftTenants.filter(t => t.id !== params.tenantId);

      await delay();
      return new HttpResponse(null, { status: exists ? 204 : 404 });
    }
  ),

  http.get('*/v2/organizations/:id/resellers/lookup', async ({ request }) => {
    const url = new URL(request.url);
    const abmResellerId = url.searchParams.get('abm_reseller_id') ?? '';
    const countryCode = url.searchParams.get('country_code') ?? 'US';
    const reseller: OrganizationResellerDto = {
      id: `organization-reseller-${abmResellerId}-${countryCode}`,
      abm_reseller_id: abmResellerId,
      country_code: countryCode,
    };
    lookedUpResellers.set(reseller.id, reseller);

    await delay();
    return HttpResponse.json(reseller, { status: 200 });
  }),

  http.get('*/v2/organizations/:id/resellers', async () => {
    await delay();
    return HttpResponse.json(organizationResellers, { status: 200 });
  }),

  // Confirms resellers previously returned by the lookup endpoint.
  http.post('*/v2/organizations/:id/resellers', async ({ request }) => {
    const body = (await request.json()) as { reseller_ids: string[] };
    const confirmed = body.reseller_ids.map(
      id =>
        lookedUpResellers.get(id) ??
        organizationResellers.find(reseller => reseller.id === id)
    );

    await delay();
    if (confirmed.some(reseller => reseller === undefined)) {
      return new HttpResponse(null, { status: 404 });
    }
    organizationResellers = confirmed.filter(
      (reseller): reseller is OrganizationResellerDto => reseller !== undefined
    );
    return HttpResponse.json(organizationResellers, { status: 200 });
  }),
];
